import { Icon16Cancel, Icon20InfoCircleOutline } from '@vkontakte/icons';
import classNames from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { fullTariff, trialTariff } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import { loadPaidInfoRequest } from 'reactApp/modules/paidInfo/paidInfo.module';
import { PaidInfoSelectors } from 'reactApp/modules/paidInfo/paidInfo.selectors';
import { loadProductsAndOpenFastCheckout } from 'reactApp/modules/payment/payment.module';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './DownloadLimitsInformer.css';

export type DownloadLimitsInformerProps = {
    isMobile?: boolean;
    totalDownloads: number;
    isHome?: boolean;
};

export const COUNT_INFORMER_SHOW = 3;
export const PERIOD = 60 * 60 * 1000;

const sendAnalytics = (action) => {
    sendDwh({ eventCategory: 'informer', action: `reach_limit_sharing_${action}` });
};

const DownloadLimitsInformer = ({ isMobile, totalDownloads, isHome }: DownloadLimitsInformerProps) => {
    const dispatch = useDispatch();
    const [isVisibleInfo, setIsVisibleInfo] = useState(false);
    const [countShow, setCountShow] = useState(0);
    const isUserWithoutPayment = useSelector(PaidInfoSelectors.isUserWithoutPayment);
    const productId = isUserWithoutPayment ? trialTariff : fullTariff;

    useEffect(() => {
        dispatch(loadPaidInfoRequest());
        emitAnalyticEvent(AnalyticEventNames.INFORMER_HOME_SHOW);
        sendAnalytics('show');
        const lastCloseDownloadInformerTime = Number(storeHelper.getValue('lastCloseDownloadInformerTime'));
        const currentTime = new Date().getTime();
        const totalShowCount = Number(storeHelper.getValue('totalShowCountDownloadInformer')) || 0;

        if (lastCloseDownloadInformerTime) {
            const elapsedTime = currentTime - lastCloseDownloadInformerTime;

            if (elapsedTime >= PERIOD && totalShowCount < COUNT_INFORMER_SHOW) {
                setIsVisibleInfo(true);
            }
        } else if (totalShowCount <= COUNT_INFORMER_SHOW) {
            setIsVisibleInfo(true);
        }

        storeHelper.setValue('totalShowCountDownloadInformer', (totalShowCount + countShow).toString());
    }, [dispatch, countShow]);

    const handleClickBye = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.INFORMER_HOME_CLICK);
        sendAnalytics('click');
        dispatch(
            loadProductsAndOpenFastCheckout({
                productId,
                paySource: 'limit_sharing_home',
                title: 'Подписка Mail Space: скачивания без лимита и другие опции',
                hideOtherTariffs: true,
            })
        );
    }, [productId]);

    const handleClickClose = () => {
        emitAnalyticEvent(AnalyticEventNames.INFORMER_HOME_CLOSE);
        sendAnalytics('close');
        setIsVisibleInfo(false);
        const currentTime = new Date().getTime().toString();
        storeHelper.setValue('lastCloseDownloadInformerTime', currentTime);
        setCountShow((prevCount) => prevCount + 1);
    };

    return (
        <>
            {isVisibleInfo && (
                <div className={classNames(styles.info, { [styles.infoMobile]: isMobile, [styles.infoHome]: isHome })}>
                    <Icon20InfoCircleOutline
                        className={classNames(styles.iconInfo, { [styles.iconInfoMobile]: isMobile, [styles.iconInfoHome]: isHome })}
                    />
                    <div className={classNames(styles.content, { [styles.contentMobile]: isMobile })}>
                        <div className={styles.text}>
                            Получатели не&nbsp;могут скачивать файлы более {totalDownloads}&nbsp;раз&nbsp;за&nbsp;сутки
                        </div>
                        {isMobile ? (
                            <a className={styles.link} onClick={handleClickBye}>
                                {isUserWithoutPayment ? 'Снять лимит за 1 ₽' : 'Снять лимит'}
                            </a>
                        ) : (
                            <Button
                                className={styles.button}
                                onClick={handleClickBye}
                                theme="octavius"
                                sizeMode={ButtonSizeMode.small}
                                data-name="secondary"
                            >
                                {isUserWithoutPayment ? 'Снять лимит за 1 ₽' : 'Снять лимит'}
                            </Button>
                        )}
                    </div>
                    <Icon16Cancel
                        onClick={handleClickClose}
                        className={classNames(styles.iconCancel, { [styles.iconCancelMobile]: isMobile })}
                    />
                </div>
            )}
        </>
    );
};

export default DownloadLimitsInformer;
